import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { AlertCircle, Copy, MoreHorizontal, Trash2, UserRoundPlus } from "lucide-react";
import { mutate } from "swr";
import { ReadOnlyBadge } from "~/components/ReadOnlyBadge";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Switch } from "~/components/ui/switch";
import useToast from "~/components/ui/toast";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { useCanManageAIAgents } from "~/pages/manifold-nextgen/hooks/useCanManageAIAgents";
import { useDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useDuplicateAgent";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import {
  Agent,
  AgentScope,
  AgentStatus,
  agentListPath,
  agentPath,
  deleteAgent,
  updateAgent,
} from "~/services/agents";
import { AgentAvatar } from "./AgentAvatar";
import { DeleteAgentDialog } from "./DeleteAgentDialog";

const NAV_ITEMS = [
  { label: "Profile", path: "profile" },
  { label: "Skills", path: "skills" },
  { label: "Toolkits", path: "toolkits" },
  { label: "Connectors", path: "connectors" },
  { label: "Featured Sessions", path: "featured-conversations" },
  { label: "Starter Prompts", path: "starter-prompts" },
  { label: "Sessions", path: "sessions" },
] as const;

export const AgentDetail = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { data: agent, error, isLoading } = useFetcher<Agent>(agentPath(agentId!));
  const canManage = useCanManageAIAgents();
  const { showSuccess, showError } = useToast();
  const duplicateAgentAction = useDuplicateAgent();

  const [enableConfirmOpen, setEnableConfirmOpen] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isEditable = !!agent && agent.scope !== AgentScope.SYSTEM;

  const headerBreadcrumbs = useMemo(
    () => [
      { label: "All Agents", href: `${ROUTE_NEXTGEN_VIEW}/agents` },
      ...(agent ? [{ label: agent.display_name }] : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agent?.display_name]
  );

  const headerActions = useMemo(
    () =>
      [
        <Button key="share" variant="outline" size="sm" className="text-xs" disabled>
          <UserRoundPlus className="size-4" />
          Share Agent
        </Button>,
      ] as [React.ReactNode?, React.ReactNode?],
    []
  );

  useNextGenHeader({ breadcrumbs: headerBreadcrumbs, actions: headerActions });

  const doToggle = async (status: AgentStatus) => {
    setIsToggling(true);
    try {
      await updateAgent(agentId!, { status });
      await Promise.all([mutate(agentPath(agentId!)), mutate(agentListPath())]);
      showSuccess(status === AgentStatus.ENABLED ? "Agent enabled" : "Agent disabled");
    } catch {
      showError("Failed to update agent status");
    } finally {
      setIsToggling(false);
      setEnableConfirmOpen(false);
    }
  };

  const handleToggleStatus = (checked: boolean) => {
    if (checked) {
      setEnableConfirmOpen(true);
    } else {
      doToggle(AgentStatus.DISABLED);
    }
  };

  const handleDuplicate = () => duplicateAgentAction(agentId!, agent!.display_name);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteAgent(agentId!);
      await mutate(agentListPath());
      showSuccess(`"${agent!.display_name}" deleted`);
      navigate(`${ROUTE_NEXTGEN_VIEW}/agents`);
    } catch {
      showError("Failed to delete agent");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-full">
        <div className="w-48 shrink-0 border-r border-border px-4 py-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="mx-1 my-1 h-8 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
        <div className="flex-1 p-6">
          <div className="h-64 animate-pulse rounded-lg bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load agent</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full overflow-hidden">
        <nav
          aria-label="Agent sections"
          className="flex w-56 shrink-0 flex-col border-r border-border"
        >
          <div className="flex flex-col gap-3 border-b border-border px-4 py-4">
            <div className="flex items-center justify-between">
              <AgentAvatar id={agent.id} name={agent.name} size={48} />
              {canManage && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 shrink-0 text-muted-foreground"
                      aria-label={`Actions for ${agent.display_name}`}
                    >
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
                    <DropdownMenuItem onClick={handleDuplicate}>
                      <Copy />
                      Duplicate
                    </DropdownMenuItem>
                    {isEditable && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                          <Trash2 />
                          Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-foreground">{agent.display_name}</p>
              {agent.scope === AgentScope.SYSTEM && (
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline" className="rounded-full">
                    System
                  </Badge>
                  <ReadOnlyBadge />
                </div>
              )}
            </div>

            {canManage && (
              <>
                <div className="-mx-4 border-t border-border" />
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">Enable</span>
                    <Switch
                      checked={agent.status === AgentStatus.ENABLED}
                      onCheckedChange={handleToggleStatus}
                      disabled={isToggling}
                      aria-label="Enable agent"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {agent.status === AgentStatus.ENABLED
                      ? "This agent is visible to users"
                      : "This agent is hidden from users"}
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col gap-1 px-4 py-3">
            {NAV_ITEMS.map(({ label, path }) => (
              <NavLink
                key={path}
                to={path}
                end
                className={({ isActive }) =>
                  cn(
                    "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                  )
                }
              >
                {label}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="flex-1 overflow-y-auto p-6">
          <Outlet context={agent} />
        </div>
      </div>

      <AlertDialog open={enableConfirmOpen} onOpenChange={setEnableConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable &ldquo;{agent.display_name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              Once enabled, this agent becomes visible to users, can be added to projects, and used
              in conversations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isToggling}>Cancel</AlertDialogCancel>
            <Button onClick={() => doToggle(AgentStatus.ENABLED)} disabled={isToggling}>
              {isToggling ? "Enabling…" : "Enable"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DeleteAgentDialog
        agent={agent}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};
